import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react';

import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  Text,
  Grid,
  Textarea,
  HStack,
  Box,
  keyframes,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import OwnSelect from 'components/OwnSelect';

import AuthContext from 'contexts/AuthContext';

import useModal from 'hooks/useModal';

import { Budget } from 'types/budget';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

type Action = Budget['flow_status']['actions'][number];

interface ModalManageFlowProps extends ModalRootProps {
  data: {
    budget: Budget;
  };
}

const spin = keyframes`
  0% {opacity: 0; scale: 0;}
  50% {opacity: 0.3;  scale: 1;}
  100% {opacity: 0;  scale: 0;}
`;

type Option = {
  label: string;
  description: string;
  color: string;
};

const formatOptionLabel = ({
  label,
  description,
  color,
}: Option): JSX.Element => {
  const spinAnimation = `${spin} infinite 2s ease`;

  return (
    <>
      <Flex direction="row" alignItems="center">
        <Box
          width={3}
          height={3}
          borderRadius="50%"
          bg={color}
          position="relative"
          boxShadow="base"
          flexShrink={0}
          _before={{
            content: '""',
            position: 'absolute',
            top: -1,
            left: -1,
            right: -1,
            bottom: -1,
            bg: color,
            opacity: 0.3,
            borderRadius: '50%',
            animation: spinAnimation,
          }}
          marginRight={2}
        />
        <b>{label}</b>
      </Flex>
      {!!description && label !== description ? description : null}
    </>
  );
};

const ModalManageFlow: React.FC<ModalManageFlowProps> = ({
  onConfirm,
  handleClose,
  data,
  ...restProps
}) => {
  const { budget } = data;

  const { openModal } = useModal();
  const { user } = useContext(AuthContext);

  const [selectedAction, setSelectedAction] = useState<Action>();

  const [observation, setObservation] = useState<string>();

  const [actions, setData] = useState<Action[]>([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | undefined>();

  const getData = useCallback(async () => {
    setLoading(true);
    setData([]);

    try {
      let actionList = null;
      if (!budget.flow_status) {
        const { data: responseFlow } = await api.get(`/flow`, {
          params: {
            page: 1,
            per_page: 1,
            'filter[organization_id]':
              user.managed_organization.organization.id,
          },
        });

        const [flow] = responseFlow.data;

        if (!flow) return;

        const response = await api.get(`/flow/action`, {
          params: {
            page: 1,
            per_page: 9999,
            'filter[flow_id]': flow.id,
          },
        });

        actionList = response.data;
      } else {
        const response = await api.get(
          `flow/status/${budget.flow_status.id}/action`,
        );

        actionList = response.data;
      }

      setData(actionList.data || []);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message || 'Houve um erro ao carregar as ações!',
        status: 'error',
      });

      setData([]);
    } finally {
      setLoading(false);
    }
  }, [budget, user]);

  useEffect(() => {
    getData();
  }, [getData]);

  const uniqueActions = useMemo(() => {
    const seenKeys = new Set<string>();

    return actions.filter((action) => {
      const targetStatusId =
        action.target_status?.id ?? action.target_status_id;
      const key = `${action.name}-${targetStatusId}`;

      if (seenKeys.has(key)) {
        return false;
      }

      seenKeys.add(key);
      return true;
    });
  }, [actions]);

  const actionsToSelect = uniqueActions.map((item) => ({
    value: item.id,
    label: item.name,
    description: item.description,
    color: item.color,
  }));

  type Client = Budget['identification']['client'];
  const clientNameByType = (client: Client): JSX.Element => {
    if (client.type_person === 'legal') {
      return (
        <>
          {client.fantasy_name}
          <br />
          <small>{client.corporate_name}</small>
        </>
      );
    }

    return <>{client.name}</>;
  };

  const getClientName = useCallback(
    (item: Budget): JSX.Element => {
      if (!item.identification) return <Text>-</Text>;

      if (item.identification.client) {
        return clientNameByType(item.identification.client);
      }

      if (item.integrated_sienge) {
        if (!item.identification.enterprise) {
          return <Text>-</Text>;
        }

        return (
          <Text as="span">
            <Text as="span" fontWeight="bold">
              {item.identification.enterprise.external_id}
            </Text>{' '}
            - {item.identification.enterprise?.name}
          </Text>
        );
      }

      return <Text>Não especificado</Text>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleConfirm = async (): Promise<void> => {
    const formErrors = {} as FormErrors;

    if (!selectedAction) {
      Object.assign(formErrors, { selectedAction: true, type: 'required' });
    }

    if (!observation) {
      Object.assign(formErrors, { observation: true, type: 'required' });
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length) {
      return;
    }

    try {
      setLoading(true);

      await api.post(`/flow/action/${selectedAction?.id}`, {
        object_type: 'budget',
        object_id: budget.id,
        flow_key: 'use_flow',
        type: 'manual',
        message: observation,
      });

      toast({
        description: `Status atualizado com sucesso!`,
        status: 'success',
      });

      if (onConfirm) onConfirm();
      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message || 'Houve um erro ao performar a ação!',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal {...restProps} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Atualizar status do orçamento</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Grid
            w="100%"
            templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
            columnGap={2}
            rowGap={4}
            paddingY={4}
            borderTop="1px solid rgba(0,0,0,0.1)"
            borderBottom="1px solid rgba(0,0,0,0.1)"
          >
            <Flex direction="row" gap={1}>
              <Text fontWeight="bold">Descrição: </Text>
              <Text
                noOfLines={1}
                textOverflow="ellipsis"
                title={budget.identification?.description}
              >
                {budget.identification.description || 'Não especificado'}
              </Text>
            </Flex>

            <Flex direction="row" gap={1}>
              <Text fontWeight="bold">Status atual: </Text>
              <Text noOfLines={1} textOverflow="ellipsis">
                {budget.flow_status?.name || 'Não especificado'}
              </Text>
            </Flex>

            <Flex direction="row" gap={1}>
              <Text fontWeight="bold">Cliente / Obra:</Text>
              <Text noOfLines={1} textOverflow="ellipsis">
                {getClientName(budget)}
              </Text>
            </Flex>

            <Flex direction="row" gap={1}>
              <Text fontWeight="bold">Total: </Text>
              <Text>
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(budget.total)}{' '}
              </Text>
            </Flex>
          </Grid>

          <FormControl marginTop="4" isInvalid={!!errors?.selectedAction}>
            <FormLabel>Ação</FormLabel>
            <OwnSelect
              placeholder="Selecione"
              options={actionsToSelect}
              value={
                selectedAction
                  ? {
                      value: selectedAction.id,
                      label: selectedAction.name,
                      description: selectedAction.description,
                      color: selectedAction.color,
                    }
                  : null
              }
              isInvalid={!!errors?.selectedAction}
              isLoading={loading}
              isDisabled={loading || !uniqueActions.length}
              isClearable
              onChange={(selected) => {
                const value = uniqueActions.find(
                  (item) => item.id === Number(selected?.value),
                );
                setSelectedAction(value);
              }}
              formatOptionLabel={formatOptionLabel}
            />
            <FormErrorMessage>Ação é obrigatória</FormErrorMessage>
          </FormControl>

          <FormControl marginTop="4" isInvalid={!!errors?.observation}>
            <FormLabel>Justificativa</FormLabel>
            <Textarea
              resize="none"
              onChange={(e) => setObservation(e.target.value)}
            />
            <FormErrorMessage>Justificativa é obrigatória</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button
              isLoading={loading}
              isDisabled={loading}
              onClick={() =>
                openModal('budgetHistory', {
                  mode: 'lookup',
                  data: {
                    budget,
                  },
                })
              }
            >
              Ver histórico
            </Button>

            <Button
              isLoading={loading}
              isDisabled={loading}
              colorScheme="green"
              onClick={handleConfirm}
            >
              Salvar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalManageFlow;
